import { ObjectId } from "@novorender/data-js-api";
import { createContext, Dispatch, MutableRefObject } from "react";

import { toIdArr, toIdObj } from "../utils";

export const initialState = {
    ids: {} as Record<ObjectId, true | undefined>,
    idArr: [] as ObjectId[],
    // Try to not change frozenIds reference because useHandleHighlight relies on it
    frozenIds: new Set<number>(),
    lastHiddenIds: [] as ObjectId[],
};

export type State = typeof initialState;

export enum ActionTypes {
    Add,
    Remove,
    SetIds,
    AddFrozen,
    SetFrozen,
    SetLastHiddenIds,
}

function add(ids: State["idArr"]) {
    return {
        type: ActionTypes.Add as const,
        ids,
    };
}

function remove(ids: State["idArr"]) {
    return {
        type: ActionTypes.Remove as const,
        ids,
    };
}

function setIds(ids: State["idArr"]) {
    return {
        type: ActionTypes.SetIds as const,
        ids,
    };
}

function addFrozen(ids: Iterable<number>) {
    return {
        type: ActionTypes.AddFrozen as const,
        ids,
    };
}

function setFrozen(ids: Iterable<number>) {
    return {
        type: ActionTypes.SetFrozen as const,
        ids,
    };
}

function setLastHiddenIds(ids: State["lastHiddenIds"]) {
    return {
        type: ActionTypes.SetLastHiddenIds as const,
        ids,
    };
}

export const actions = { add, remove, setIds, addFrozen, setFrozen, setLastHiddenIds };

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchHidden = Dispatch<Actions>;
export type LazyState = MutableRefObject<State>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyStateContext = createContext<LazyState>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchHidden>(undefined as any);

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.Add: {
            const ids = { ...state.ids, ...toIdObj(action.ids) };

            return {
                ids,
                idArr: toIdArr(ids),
                frozenIds: state.frozenIds,
                lastHiddenIds: state.lastHiddenIds,
            };
        }
        case ActionTypes.Remove: {
            action.ids.forEach((id) => {
                delete state.ids[id];
            });

            let newFrozenIds = state.frozenIds;
            if (action.ids.some((id) => state.frozenIds.has(id))) {
                newFrozenIds = new Set<number>(state.frozenIds);
                for (const id of action.ids) {
                    newFrozenIds.delete(id);
                }
            }

            return {
                ids: { ...state.ids },
                idArr: toIdArr(state.ids),
                frozenIds: newFrozenIds,
                lastHiddenIds: state.lastHiddenIds,
            };
        }
        case ActionTypes.SetIds: {
            let newFrozenIds = new Set<number>();
            for (const id of action.ids) {
                if (state.frozenIds.has(id)) {
                    newFrozenIds.add(id);
                }
            }
            if (newFrozenIds.size === state.frozenIds.size) {
                newFrozenIds = state.frozenIds;
            }

            return {
                ids: toIdObj(action.ids),
                idArr: action.ids,
                frozenIds: newFrozenIds,
                lastHiddenIds: state.lastHiddenIds,
            };
        }
        case ActionTypes.AddFrozen: {
            const newFrozenIds = new Set(state.frozenIds);
            for (const id of action.ids) {
                newFrozenIds.add(id);
            }
            if (newFrozenIds.size === state.frozenIds.size) {
                return state;
            }

            return {
                ...state,
                frozenIds: newFrozenIds,
                lastHiddenIds: state.lastHiddenIds,
            };
        }
        case ActionTypes.SetFrozen: {
            const newFrozenIds = new Set(action.ids);
            return {
                ...state,
                frozenIds: newFrozenIds,
                lastHiddenIds: state.lastHiddenIds,
            };
        }
        case ActionTypes.SetLastHiddenIds: {
            return {
                ...state,
                lastHiddenIds: action.ids,
            };
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
